import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AppConfigService } from '../config/config.public-api';

import type { NotificationListResult } from './notifications.model';
import type { NotificationPageRequest, NotificationsPort } from './notifications.port';
import type { NotificationListResponseDto } from './notifications.dto';
import { toDomainNotifications } from './notifications.mapper';

/**
 * INFRASTRUCTURE — Adapter HTTP que implementa el `NotificationsPort`.
 *
 * Única pieza que conoce HttpClient y la forma del contrato. El bearer, el tenant
 * y el trace-id los agregan los interceptores de `core/http`. La base URL sale del
 * `config.json` de runtime (`apiBaseUrl`, ya versionada en `/api/v1`), así que el
 * endpoint efectivo es `${apiBaseUrl}/notifications`.
 *
 * `providedIn: 'root'`: la campana es chrome del shell (topbar), no una ruta lazy;
 * necesita un singleton disponible fuera de cualquier feature.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsHttpAdapter implements NotificationsPort {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);

  private get baseUrl(): string {
    return `${this.appConfig.config().apiBaseUrl}/notifications`;
  }

  async list(pageRequest: NotificationPageRequest): Promise<NotificationListResult> {
    // Paginación EXPLÍCITA: sin estos params la página real dependía del default
    // del server (truncado silencioso latente, QA round 2).
    const params = new HttpParams()
      .set('page', pageRequest.page)
      .set('pageSize', pageRequest.pageSize);
    const res = await firstValueFrom(
      this.http.get<NotificationListResponseDto>(this.baseUrl, { params }),
    );
    return { items: toDomainNotifications(res.items), total: res.total };
  }
}
